/**
 * WeekSummary — header strip rendered above each week's day grid in the
 * rides calendar.
 *
 * Shows quick totals (count / hours / km / TSS) always. When the week
 * has rides:
 *   - If a coach summary is cached, render a 2-3 sentence preview plus
 *     a link to the full /weeks/<iso> page.
 *   - If no summary cached, show an "Analyze this week" button that
 *     calls POST /weeks/<iso>/summary/generate.
 *   - During generation, show a loading state in place of the button.
 *
 * Empty weeks render a thin spacer so the calendar's vertical rhythm
 * stays consistent without a noisy "0 rides" strip.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GarminAiAttribution } from './GarminAttribution.tsx';
import type { RideListItem } from '../types/api.ts';
import {
  useGenerateWeeklySummary,
  useWeeklySummary,
} from '../hooks/useWeeklySummary.ts';
import { previewFromSummary } from '../lib/weeklySummaryPreview.ts';

export interface CalDay {
  iso: string;
  day: number;
  month: number;
}

interface WeekSummaryProps {
  week: CalDay[];
  ridesByDate: Map<string, RideListItem[]>;
}

export function WeekSummary({ week, ridesByDate }: Readonly<WeekSummaryProps>) {
  const { t } = useTranslation();
  const totals = useMemo(() => {
    let count = 0;
    let durationSec = 0;
    let distanceM = 0;
    let tss = 0;
    for (const day of week) {
      const rides = ridesByDate.get(day.iso) ?? [];
      for (const r of rides) {
        count += 1;
        durationSec += r.duration_sec ?? 0;
        distanceM += r.distance_m ?? 0;
        tss += r.training_stress_score ?? 0;
      }
    }
    return { count, durationSec, distanceM, tss };
  }, [week, ridesByDate]);

  // Monday of this calendar week — buildMonthGrid returns days
  // Monday-first, so week[0].iso is always the Monday ISO date.
  const monday = week[0]?.iso ?? null;
  // Only hit the backend when the week has at least one ride; empty
  // weeks aren't generatable and showing a button on them would be a
  // dead UI affordance.
  const shouldFetchSummary = totals.count > 0 && !!monday;
  const summaryQuery = useWeeklySummary(shouldFetchSummary ? monday : null);
  const generateSummary = useGenerateWeeklySummary();

  if (totals.count === 0) {
    return <div className="h-1" />;
  }

  const cached = summaryQuery.data?.summary ?? null;
  const isGenerating = generateSummary.isPending && generateSummary.variables === monday;
  const preview = cached ? previewFromSummary(cached) : null;

  return (
    <div className="rounded-lg bg-(--color-bg-tertiary)/60 border border-(--color-border)/60 px-3 py-2 flex flex-col gap-1.5 text-[10px] sm:text-[11px] text-(--color-text-secondary)">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
        <span className="font-medium text-(--color-text-primary)">
          {totals.count} {t(totals.count === 1 ? 'week.rideSingular' : 'week.ridePlural')}
        </span>
        <span>·</span>
        <span>{(totals.durationSec / 3600).toFixed(1)}h</span>
        <span>·</span>
        <span>{(totals.distanceM / 1000).toFixed(0)} km</span>
        <span>·</span>
        <span>TSS {Math.round(totals.tss)}</span>
      </div>

      {preview && (
        <div className="flex flex-col gap-1">
          <p className="text-(--color-text-primary) text-[11px] sm:text-xs leading-snug">
            {preview}
          </p>
          <GarminAiAttribution sources={summaryQuery.data?.ai_sources} />
          {monday && (
            <Link
              to={`/weeks/${monday}`}
              className="text-emerald-400 hover:text-emerald-300 text-[10px] sm:text-[11px] font-medium self-start"
            >
              {t('week.openFullWeek')}
            </Link>
          )}
        </div>
      )}

      {!preview && monday && (
        <div>
          <button
            type="button"
            onClick={() => generateSummary.mutate(monday)}
            disabled={isGenerating}
            className="text-emerald-400 hover:text-emerald-300 disabled:opacity-50 text-[10px] sm:text-[11px] font-medium border border-emerald-400/40 hover:border-emerald-300/60 rounded-full px-3 py-1 transition-colors"
          >
            {isGenerating ? t('week.generating') : t('week.analyzeWeek')}
          </button>
        </div>
      )}
    </div>
  );
}
